import { useState } from 'react'

import { CreatorPermission, TeamMemberRole } from '@api'
import type { Row } from '@tanstack/table-core'
import { useUnit } from 'effector-react'
import { useNavigate } from 'react-router'

import KeyIcon from '@/assets/icons/icon-key.svg'
import ExpandMoreIcon from '@/assets/icons/icon-more.svg'
import ProxyIcon from '@/assets/icons/icon-proxy-configuration.svg'
import StayUndercoverIcon from '@/assets/icons/icon-stayundercover.svg'
import TrashIcon from '@/assets/icons/icon-trash.svg'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.tsx'
import { $account } from '@/models/account.ts'
import { StartOnlyFansButton } from '@/modules/creators/ui/StartOnlyFansButton.tsx'
import { useDesktopApp } from '@/modules/desktop/DesktopAppProvider.tsx'
import { AppRoutes } from '@/utils/constants'

import type { Creator } from '../types'
import { ProxyConfigurationDialog } from './Dialogs/ProxyConfigurationDialog'
import { RemoveCreatorDialog } from './Dialogs/RemoveCreatorDialog'
import { StayUndercoverDialog } from './Dialogs/StayUndercoverDialog'

export interface CreatorActionsProps {
  row: Row<Creator>
}

export const CreatorActions = ({ row }: CreatorActionsProps) => {
  const creator = row.original
  const { isApp } = useDesktopApp()
  const [showRemoveDialog, setShowRemoveDialog] = useState(false)
  const [showProxyConfigurationDialog, setShowProxyConfigurationDialog] =
    useState(false)
  const [showStayUndercoverDialog, setShowStayUndercoverDialog] =
    useState(false)
  const [firstLaunch, setFirstLaunch] = useState(false)

  const navigate = useNavigate()

  const account = useUnit($account)
  const isLeader = account?.memberRole === TeamMemberRole.LEADER

  const isAccessPageAccessible = [
    TeamMemberRole.ADMIN,
    TeamMemberRole.LEADER,
  ].some((role) => role === account?.memberRole)

  const openRemoveDialog = () => {
    setShowRemoveDialog(true)
  }

  const openStayUndercoverDialog = () => {
    setShowStayUndercoverDialog(true)
  }

  const openProxyConfigurationDialog = (firstLaunch: boolean) => {
    setFirstLaunch(firstLaunch)
    setShowProxyConfigurationDialog(true)
  }

  const navigateToAccessPage = () => {
    navigate(AppRoutes.Creators.Access.asUrl(String(row.original.id)))
  }

  const itemClassNames =
    'h-[32px] gap-[8px] rounded-[6px] px-[16px] py-[8px] hover:bg-primary-500 hover:text-neutral-default disabled:opacity-60'

  return (
    <div className="flex justify-end gap-8">
      <DropdownMenu>
        <DropdownMenuTrigger>
          <ExpandMoreIcon className="h-5 w-5" />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-50 px-[5px] py-[8px]"
        >
          {creator.permissions.includes(CreatorPermission.MODIFY_SETTINGS) &&
            isApp && (
              <DropdownMenuItem
                onClick={() =>
                  openProxyConfigurationDialog(creator.proxySettingsOnLaunch)
                }
                className={itemClassNames}
              >
                <ProxyIcon className="h-4 w-4" />
                Proxy Configuration
              </DropdownMenuItem>
            )}
          <DropdownMenuItem
            asChild
            className="w-full"
          >
            <StartOnlyFansButton
              creator={creator}
              openProxyConfigurationDialog={openProxyConfigurationDialog}
              className={itemClassNames}
            />
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={openStayUndercoverDialog}
            className={itemClassNames}
          >
            <StayUndercoverIcon className="h-4 w-4" />
            StayUndercover
          </DropdownMenuItem>
          {isAccessPageAccessible && (
            <DropdownMenuItem
              className={itemClassNames}
              onClick={navigateToAccessPage}
            >
              <KeyIcon className="h-4 w-4" />
              Access
            </DropdownMenuItem>
          )}

          {creator.deletable && isLeader && (
            <DropdownMenuItem
              onClick={openRemoveDialog}
              disabled={!creator.deletable}
              className={
                itemClassNames +
                ' hover:bg-danger-default hover:text-neutral-default'
              }
            >
              <div className="flex h-4 w-4 items-center justify-center">
                <TrashIcon />
              </div>
              Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <RemoveCreatorDialog
        row={row}
        setShowRemoveDialog={setShowRemoveDialog}
        showRemoveDialog={showRemoveDialog}
      />

      <StayUndercoverDialog
        row={row}
        setShowStayUndercoverDialog={setShowStayUndercoverDialog}
        showStayUndercoverDialog={showStayUndercoverDialog}
      />

      <ProxyConfigurationDialog
        firstLaunch={firstLaunch}
        setShowProxyConfigurationDialog={setShowProxyConfigurationDialog}
        showProxyConfigurationDialog={showProxyConfigurationDialog}
        row={row}
      />
    </div>
  )
}
